import {
  type BreakdownOption,
  type CustomBreakdown,
  NUMERIC_FILTER_FIELDS,
  NUMERIC_FILTER_LABELS,
} from 'features/charts-v2/types'
import type { LeftSidebarItem } from 'features/charts-v2/utils/is-sidebar-item-selected'
import { type FilterOption, FilterTypes } from 'features/filters/types'

type GroupId =
  | 'saved'
  | 'hub'
  | 'threshold'
  | 'language'
  | 'search'
  | 'source'
  | 'products'
  | 'posts'

const GROUP_HEADERS: Record<GroupId, { name: string; description: string }> = {
  saved: { name: 'Saved', description: 'Your saved filter combinations.' },
  hub: {
    name: 'Hub',
    description: 'Defines what data your analysis is pulling from.',
  },
  threshold: {
    name: 'Threshold',
    description:
      'Filter products or themes by how many records or sentences are behind them.',
  },
  language: {
    name: 'Language',
    description: "What Yogi's AI figured out about your records.",
  },
  search: {
    name: 'Search',
    description:
      'Find records by exact words or AI-powered natural language search.',
  },
  source: { name: 'Source', description: 'Where the data was collected.' },
  products: {
    name: 'Products',
    description:
      'Attributes of the product itself — same across every record about it.',
  },
  posts: {
    name: 'Posts',
    description:
      'Attributes that vary record-by-record — even for the same source/product.',
  },
}

// Ids compare through `normalizeFieldId` because custom fields carry the
// project's own spelling ("Syndicated On" vs `syndicated_on`, "Verified" vs
// `verified`) while the standard ones are snake_case.
const normalizeFieldId = (field: string): string =>
  field.toLowerCase().replace(/[^a-z0-9]/g, '')

// The groups a data field can sit in. Saved, Threshold and Search hold only the
// picker's own rows (saved filters, Records / Sentences, Keywords / Smart
// search), never a field option.
type FieldGroupId = 'hub' | 'language' | 'source' | 'products' | 'posts'

// Standard + well-known fields, in the order they sort within their group.
// Anything not listed is a custom field and falls to its `model` (see
// `groupForField`), A-Z after these.
const PINNED_FIELDS: Record<FieldGroupId, string[]> = {
  hub: ['proj_uuid'],
  language: ['parent_theme', 'theme', 'theme_group'],
  source: ['source', 'source_type', 'syndicated_on'],
  products: ['brand', 'product', 'product_group', 'product_hierarchy'],
  posts: [
    'country',
    'post_score',
    'promotion',
    'recommended',
    'score',
    'verified',
  ],
}

const PINNED_GROUP_BY_FIELD = new Map<string, FieldGroupId>(
  Object.entries(PINNED_FIELDS).flatMap(([group, fields]) =>
    fields.map((field) => [normalizeFieldId(field), group as FieldGroupId]),
  ),
)

const pinnedRank = (field: string): number => {
  const normalized = normalizeFieldId(field)
  const group = PINNED_GROUP_BY_FIELD.get(normalized)
  if (!group) return Number.POSITIVE_INFINITY
  return PINNED_FIELDS[group].findIndex(
    (pinned) => normalizeFieldId(pinned) === normalized,
  )
}

// `model` is set on custom fields only (`include_model`). A custom field with
// no model is treated as a product attribute, mirroring `mapFieldToDimension`.
const GROUP_BY_MODEL: Record<string, FieldGroupId> = {
  SourceModel: 'source',
  ProductModel: 'products',
  PostModel: 'posts',
  PostTextModel: 'posts',
}

const groupForField = (field: string, model?: string): FieldGroupId =>
  PINNED_GROUP_BY_FIELD.get(normalizeFieldId(field)) ??
  (model ? GROUP_BY_MODEL[model] : undefined) ??
  'products'

// A filter field's group name ("Hub", "Language", …) — what a filter card shows
// as its `Group >` prefix, so the card names the same group the picker lists it
// under.
export const getFilterGroupLabel = (field: string, model?: string): string =>
  GROUP_HEADERS[groupForField(field, model)].name

// Every breakdown the picker offers, and the group its field sits in there. A
// `Record` so a new breakdown id cannot be added without choosing a group.
const GROUP_BY_BREAKDOWN: Record<BreakdownOption, GroupId> = {
  brand: 'products',
  product: 'products',
  country: 'posts',
  promotional: 'posts',
  rating: 'posts',
  theme: 'language',
  sentiment: 'language',
  source_type: 'source',
  dataset: 'hub',
}

const GROUP_BY_CUSTOM_BREAKDOWN_MODEL: Record<
  CustomBreakdown['model'],
  GroupId
> = {
  product: 'products',
  source: 'source',
  post: 'posts',
  post_text: 'posts',
}

export const getBreakdownGroupLabel = (breakdown: BreakdownOption): string =>
  GROUP_HEADERS[GROUP_BY_BREAKDOWN[breakdown]].name

export const getCustomBreakdownGroupLabel = (
  model: CustomBreakdown['model'],
): string => GROUP_HEADERS[GROUP_BY_CUSTOM_BREAKDOWN_MODEL[model]].name

// The group names the special (non-field) cards sit under in the picker:
// Records / Sentences, Sentiment, and Keywords / Smart search.
export const FILTER_GROUP_LABELS = {
  threshold: GROUP_HEADERS.threshold.name,
  language: GROUP_HEADERS.language.name,
  search: GROUP_HEADERS.search.name,
}

// Hover copy for the individual rows that are ambiguous. Keyed by normalized
// field id for field rows; the pane rows are keyed in `PANE_DESCRIPTIONS`.
const FIELD_DESCRIPTIONS: Record<string, string> = {
  projuuid: 'Which dataset within this hub to pull from.',
  theme: 'Narrow to records tagged with specific Yogi themes.',
  sourcetype: 'The platform or site the record was collected from.',
  source: 'The specific instance within that platform.',
  country: 'Where the record originated.',
  syndicatedon: 'Retailer sites this review was syndicated to.',
}

const PANE_DESCRIPTIONS = {
  sentiment: "Yogi's AI sentiment score. Higher = more positive.",
  record_count:
    'Filter products or themes by the number of records behind them.',
  sentence_count:
    'Filter products or themes by the number of sentences behind them.',
  keywords: 'Find records containing exact words or phrases.',
  smart_search:
    'Describe what you want in plain English — AI finds similar records.',
} as const

const group = (id: GroupId): LeftSidebarItem => ({
  type: 'group',
  ...GROUP_HEADERS[id],
})

// `#` for numeric filters, `Aa` for text ones — the two kinds of value a
// filter takes, so a reader can tell a min/max window from a value list.
const fieldItem = (option: FilterOption): LeftSidebarItem => ({
  name: option.alias || option.field,
  field: option.field,
  type: 'field',
  glyph: option.filter_type === FilterTypes.SELECT ? 'Aa' : '#',
  description: FIELD_DESCRIPTIONS[normalizeFieldId(option.field)],
})

const byPinnedThenAlias = (a: FilterOption, b: FilterOption): number =>
  pinnedRank(a.field) - pinnedRank(b.field) ||
  (a.alias || a.field).localeCompare(b.alias || b.field)

/**
 * The full (non-`fieldsOnlySidebar`) left column of `FilterSelectionPopover`:
 * "All", then subject groups, each led by a header row. A group with no rows
 * is dropped. Sentiment is the pane row, so the `sentiment` / `post_sentiment`
 * field options are skipped here.
 */
export const buildGroupedSidebarItems = (
  filterOptions: FilterOption[],
): LeftSidebarItem[] => {
  const fields = filterOptions.filter(
    (option) =>
      option.field !== 'sentiment' && option.field !== 'post_sentiment',
  )
  const fieldsIn = (id: FieldGroupId): LeftSidebarItem[] =>
    fields
      .filter((option) => groupForField(option.field, option.model) === id)
      .sort(byPinnedThenAlias)
      .map(fieldItem)

  const sections: Array<[GroupId, LeftSidebarItem[]]> = [
    ['saved', [{ name: 'Saved filters', type: 'saved', glyph: '★' }]],
    ['hub', fieldsIn('hub')],
    [
      'threshold',
      NUMERIC_FILTER_FIELDS.filter((f) => f !== 'sentiment_filter').map(
        (field) => ({
          name: NUMERIC_FILTER_LABELS[field],
          type: 'numeric' as const,
          numericField: field,
          glyph: '#' as const,
          description:
            PANE_DESCRIPTIONS[field as keyof typeof PANE_DESCRIPTIONS],
        }),
      ),
    ],
    [
      'language',
      [
        ...fieldsIn('language'),
        {
          name: 'Sentiment',
          type: 'sentiment',
          glyph: '#',
          description: PANE_DESCRIPTIONS.sentiment,
        },
      ],
    ],
    [
      'search',
      [
        {
          name: 'Keywords',
          type: 'keywords',
          glyph: 'Aa',
          description: PANE_DESCRIPTIONS.keywords,
        },
        {
          name: 'Smart search',
          type: 'smart_search',
          glyph: 'Aa',
          description: PANE_DESCRIPTIONS.smart_search,
        },
      ],
    ],
    ['source', fieldsIn('source')],
    ['products', fieldsIn('products')],
    ['posts', fieldsIn('posts')],
  ]

  return [
    { name: 'All', type: 'category', glyph: '◉' },
    ...sections.flatMap(([id, rows]) =>
      rows.length > 0 ? [group(id), ...rows] : [],
    ),
  ]
}
